import { CategorieActivite } from '../../entreprise/infrastructure/entreprise.definition';
export enum ControleQuinquennalStatus {
  EN_COURS = 'EN_COURS',
  DEPOSE = 'DEPOSE',
  TERMINE = 'TERMINE',
  ANNULE = 'ANNULE',
}

export enum OriasStatut {
  INSCRIT = 'INSCRIT',
  SUPPRIME = 'SUPPRIME',
  JAMAIS_INSCRIT = 'JAMAIS_INSCRIT',
}

export enum ControleQuinquennalAxeType {
  ASSUJETTI = 'ASSUJETTI',
  RCPRO_COA = 'RCPRO_COA',
  RCPRO_MIA = 'RCPRO_MIA',
  RCPRO_COBSP = 'RCPRO_COBSP',
  RCPRO_MIOBSP = 'RCPRO_MIOBSP',
  GARANTIE_FINANCIERE_IAS = 'GARANTIE_FINANCIERE_IAS',
  GARANTIE_FINANCIERE_IOBSP = 'GARANTIE_FINANCIERE_IOBSP',
}

export enum ControleQuinquennalAxeStatus {
  EN_COURS = 'EN_COURS',
  VALIDE = 'VALIDE',
  REMEDIE = 'REMEDIE',
}

export const CONTROLE_QUINQUENNAL_AXE_FILE_UPLOADERS = [
  'ADHERENT',
  'VERIFICATEUR',
] as const;

export type ControleQuinquennalAxeFileUploader =
  (typeof CONTROLE_QUINQUENNAL_AXE_FILE_UPLOADERS)[number];

/**
 * État du contrôle au dépôt d'une pièce, figé sur elle : avant toute
 * remédiation, pendant l'une d'elles, ou après sa clôture.
 */
export const CONTROLE_QUINQUENNAL_AXE_FILE_CONTEXTES_DEPOT = [
  'INITIAL',
  'REMEDIATION',
  'DEPOT',
] as const;

export type ControleQuinquennalAxeFileContexteDepot =
  (typeof CONTROLE_QUINQUENNAL_AXE_FILE_CONTEXTES_DEPOT)[number];

/**
 * Issue de la classification d'une pièce. `PENDING` est l'état d'attente entre
 * le dépôt et le verdict (OCR puis LLM) ; `TECH_ERROR` est réessayable et
 * laisse la pièce exploitable via son type déclaré.
 */
export const CONTROLE_QUINQUENNAL_CLASSIFICATION_STATUTS = [
  'PENDING',
  'CLASSIFIED',
  'UNKNOWN',
  'UNREADABLE',
  'TECH_ERROR',
] as const;

export type ControleQuinquennalClassificationStatus =
  (typeof CONTROLE_QUINQUENNAL_CLASSIFICATION_STATUTS)[number];

/**
 * Type déclaré pour une pièce d'un dépôt multiple, rattaché par nom de fichier :
 * l'ordre des parties d'un multipart ne se prête pas à un rattachement par
 * index. Deux pièces homonymes d'un même envoi consomment les déclarations de
 * même nom dans l'ordre d'apparition.
 */
export type TypePieceDeclaration = {
  filename: string;
  typePieceDeclare: string;
};

export enum VerificationStatus {
  PENDING = 'PENDING',
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
}

export enum AnalyseRunScope {
  DOSSIER = 'DOSSIER',
  AXE = 'AXE',
  SOUS_AXE = 'SOUS_AXE',
}

export const SOCIETE_AXE_SUBAXIS = '';

/**
 * Au-delà de ce délai, une vérification restée PENDING est considérée orpheline
 * (aucun résultat revenu : crash, DLQ, analyse indisponible) : la gateway
 * autorise de nouveau une relance et l'IA n'est plus annoncée « en cours ».
 */
export const STALE_PENDING_ANALYSIS_MS = 15 * 60 * 1000;

/**
 * Ligne de vérification explicite fournie par l'appelant (fan-out gateway, MR-E).
 * `status` limité à PENDING (sous-axe documentaire, message SQS à venir) ou
 * SUCCESS (sous-axe résolu par règles, ligne créée directement en état terminal).
 */
export type ControleQuinquennalPendingVerificationRow = {
  subAxis: string;
  assujettiId?: string;
  dimension?: string;
  preVerdict?: unknown;
  status?: VerificationStatus.PENDING | VerificationStatus.SUCCESS;
  resultPayload?: unknown;
};

/**
 * Corps optionnel de `POST …/verifications`. Absent → une ligne '' PENDING par
 * axe (comportement legacy, inchangé). Présent → lignes explicites par axe
 * (form-api ne dérive alors PLUS la ligne '' pour ces axes).
 */
export type ControleQuinquennalCreatePendingVerificationsBody = {
  axes?: {
    idAxe: string;
    rows: ControleQuinquennalPendingVerificationRow[];
  }[];
};

/**
 * Sous-axe PENDING orphelin (fan-out MR-E), renvoyé au balayage périodique de la
 * gateway. Ne concerne QUE les lignes de sous-axes (`subAxis != ''`) : les lignes
 * '' des axes société gardent leur cycle de vie propre.
 */
export type StalePendingVerification = {
  idControleQuinquennal: string;
  idAxe: string;
  subAxis: string;
  dateDepot: string;
  startedAt: string;
};

// Miroir partiel des types analyse (resultPayload CQ), maintenu à la main car
// form-api n'a aucune dépendance sur `analyse` (archi unidirectionnelle).
// KEEP IN SYNC with analyse/src/app/controle_quinquennal/dto/cq.dto.ts ;
// cohérence gardée par controle-quinquennal.symmetry.spec.ts (test structurel).

// Miroir de analyse `ControleQuinquennalGuardrail`.
export type ControleQuinquennalGuardrail = 'PROMPT_LEAK';

// Miroir de analyse `ControleQuinquennalAnswer`.
export type ControleQuinquennalAiAnswer = {
  questionId: string;
  // option label OR a sentinel: 'INDETERMINE' | 'MANIPULATION_SUSPECTEE'
  answer: string;
  justification: string;
  evidence?: string;
  confidence?: 'faible' | 'moyenne' | 'elevee';
  guardrail?: ControleQuinquennalGuardrail;
  evidenceGrounded?: boolean;
};

// Miroir de analyse `ControleQuinquennalDimension`.
export type ControleQuinquennalDimension =
  | 'HONORABILITE'
  | 'CAPACITE_PRO_IAS'
  | 'CAPACITE_PRO_IOBSP'
  | 'FORMATION_CONTINUE_IAS'
  | 'FORMATION_CONTINUE_IOBSP'
  | 'LISTE_ASSUJETTIS';

// Miroir de analyse `ControleQuinquennalSujet` (identité du sous-axe, écho dans
// resultPayload).
export type ControleQuinquennalSujet = {
  type: 'ASSUJETTI' | 'AXE';
  assujettiId?: string;
  dimension: ControleQuinquennalDimension;
  questionnaireSlug: string;
  displayName?: string;
};

// Verdict tripolaire (conception §2.2). Produit par la fusion règles+LLM côté
// gateway (MR-E) et écrit dans resultPayload.verdict ; typé ici pour la lecture
// (route admin + front-vérificateur). Additif.
export type ControleQuinquennalEcartType =
  | 'DECLARATION_INCOMPLETE'
  | 'PIECE_MANQUANTE'
  | 'PIECE_NON_RATTACHABLE'
  | 'INCOHERENCE_DECLARE_PIECE'
  | 'SEUIL_NON_ATTEINT'
  | 'HORS_PERIMETRE_DOCUMENTAIRE'
  | 'A_QUALIFIER';

export type ControleQuinquennalStatutSuggere =
  'CONFORME_SUGGERE' | 'A_EXAMINER' | 'NON_CONTROLE_IA';

export type ControleQuinquennalVerdictSousAxe = {
  statutSuggere: ControleQuinquennalStatutSuggere;
  ecarts: { type: ControleQuinquennalEcartType; detail: string }[];
  regles: {
    requis: Record<string, unknown>;
    declareConforme: boolean | null;
    version: string;
  };
};

// Miroir de analyse `ControleQuinquennalUnreadReason`.
export type ControleQuinquennalUnreadReason =
  | 'OCR_FAILED'
  | 'OCR_EMPTY'
  | 'EXCLUDED_OTHER_PERSON'
  | 'EXCLUDED_TYPE_FILTER'
  | 'UNSUPPORTED_FORMAT'
  | 'TOO_LARGE';

// Miroir de analyse `ControleQuinquennalUnreadDocument` : pièce déposée mais
// absente du corpus analysé.
export type ControleQuinquennalUnreadDocument = {
  idFile: string;
  filename: string;
  reason: ControleQuinquennalUnreadReason;
};

// Miroir (partiel) du resultPayload analyse. `sujet`/`source`/`verdict` sont des
// enrichissements additifs écrits par la fusion gateway (MR-E) ; optionnels pour
// que les payloads RC Pro / GF actuels restent valides.
// `unreadDocuments`/`analyzedDocumentCount`/`totalDocumentCount` sont additifs et
// absents des payloads antérieurs : une lecture doit toujours tolérer `undefined`.
export type ControleQuinquennalAiResult = {
  axeType?: string;
  answers: ControleQuinquennalAiAnswer[];
  warnings?: string[];
  sujet?: ControleQuinquennalSujet;
  source?: 'LLM' | 'RULES';
  verdict?: ControleQuinquennalVerdictSousAxe;
  unreadDocuments?: ControleQuinquennalUnreadDocument[];
  analyzedDocumentCount?: number;
  totalDocumentCount?: number;
};

// ---- Estimation coût / durée analyse CQ (conception EN-1476) ----

// Miroir de analyse `ControleQuinquennalRunUsage`.
// KEEP IN SYNC with analyse/src/app/controle_quinquennal/dto/cq.dto.ts ;
// cf. controle-quinquennal.symmetry.spec.ts pour le reste des miroirs.
export type ControleQuinquennalAnalyseRunUsage = {
  inputTokens: number;
  outputTokens: number;
  // null — jamais 0 — quand le tarif du modèle est inconnu.
  llmPriceUsd: number | null;
  ocrPriceUsd: number;
  ocrPages: number;
  llmProvider: string;
  llmModelId: string;
};

export type ControleQuinquennalAnalyseRun = {
  id: string;
  launchId: string;
  idControleQuinquennal: string;
  idAxe: string;
  subAxis: string;
  dateDepot: Date;
  scope: AnalyseRunScope;
  status: VerificationStatus;
  startedAt: Date;
  endedAt: Date | null;
  documentCount: number;
  inputTokens: number | null;
  outputTokens: number | null;
  llmPriceUsd: number | null;
  ocrPriceUsd: number | null;
  ocrPages: number | null;
  llmProvider: string | null;
  llmModelId: string | null;
  createdAt: Date;
};

// Une ligne PENDING d'un lancement (§3.3 POST .../analyse-runs). `dateDepot` et
// `startedAt` voyagent en ISO string (corps JSON), converties en Date côté
// technical-service.
export type ControleQuinquennalAnalyseRunRowInput = {
  idAxe: string;
  subAxis: string;
  dateDepot: string;
  scope: AnalyseRunScope;
  startedAt: string;
  documentCount: number;
};

export type ControleQuinquennalCreateAnalyseRunsBody = {
  launchId: string;
  rows: ControleQuinquennalAnalyseRunRowInput[];
};

// Corps de PUT .../analyse-runs/complete. `launchId` optionnel : absent d'une
// gateway pas encore déployée (rolling deploy) → no-op silencieux (§5 du
// contrat), jamais une erreur.
export type ControleQuinquennalCompleteAnalyseRunBody = {
  launchId?: string;
  idAxe: string;
  subAxis: string;
  status: VerificationStatus.SUCCESS | VerificationStatus.FAILED;
  endedAt: string;
  usage?: ControleQuinquennalAnalyseRunUsage;
};

export type ControleQuinquennalCompleteAnalyseRunResult = {
  // false = no-op silencieux (launchId absent ou lancement/ligne inconnu(e)).
  updated: boolean;
};

// Sommes d'une régression linéaire simple (une variable x), non résolues côté
// form-api : la résolution (pente/ordonnée, gardes déterminant nul) est un
// souci gateway (§2 du contrat).
export type OlsSums = {
  n: number;
  sumX: number;
  sumXX: number;
  sumY: number;
  sumXY: number;
};

// Sommes d'une régression à 2 prédicteurs (N, Σdocs), maille lancement.
export type AnalyseRunDurationSums = {
  n: number;
  sumN: number;
  sumD: number;
  sumNN: number;
  sumDD: number;
  sumND: number;
  sumY: number;
  sumNY: number;
  sumDY: number;
};

export type AnalyseRunStats = {
  llmCost: OlsSums;
  ocrCost: OlsSums;
  duration: AnalyseRunDurationSums;
};

export type AnalyseRunStatsFilters = {
  llmProvider?: string;
  llmModelId?: string;
};

/**
 * Entreprise d'un contrôle, telle que la gateway la résout depuis HubSpot.
 * form-api ne sait pas la retrouver seule : `EntrepriseVersion` n'archive que
 * les millésimes révolus (la gateway lit HubSpot pour le millésime courant, cf.
 * `entreprise.service.ts`), or un contrôle porte toujours sur le millésime
 * courant. L'y chercher ne ramenait rien, et les questionnaires des assujettis
 * disparaissaient silencieusement du total.
 */
export type EntrepriseDuControle = {
  siren: string;
  categoriesActivites: CategorieActivite[];
  categoriesActivitesAccessoires: CategorieActivite[];
};
